import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Printer, FileBarChart } from "lucide-react";

const AFN = (n) => `${Number(n || 0).toLocaleString()} AFN`;

const REPORTS = [
  { key: "students", label: "Students" },
  { key: "attendance", label: "Attendance" },
  { key: "fees", label: "Fees & payments" },
  { key: "outstanding", label: "Outstanding balances" },
  { key: "classes", label: "Classes" },
  { key: "progress", label: "Student progress" },
  { key: "certificates", label: "Certificates" },
];

const COLUMNS = {
  students: [["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["course", "Course"],
    ["classes", "Classes"], ["active", "Active"]],
  attendance: [["name", "Student"], ["classes", "Classes"], ["present", "Present"], ["absent", "Absent"],
    ["late", "Late"], ["excused", "Excused"], ["total", "Records"], ["percentage", "Attendance %"]],
  fees: [["student_name", "Student"], ["category", "Category"], ["net_amount", "Net"],
    ["paid_amount", "Paid"], ["balance", "Balance"], ["status", "Status"], ["due_date", "Due"]],
  outstanding: [["student_name", "Student"], ["outstanding", "Outstanding"],
    ["unpaid_items", "Unpaid items"], ["overdue_items", "Overdue"], ["oldest_due_date", "Oldest due"]],
  classes: [["name", "Class"], ["course", "Course"], ["teacher", "Teacher"], ["branch", "Branch"],
    ["room", "Room"], ["students", "Students"], ["days", "Days"], ["start_time", "Start"],
    ["attendance_records", "Attendance"], ["homework", "Homework"], ["exams", "Exams"]],
  progress: [["student_name", "Student"], ["class_name", "Class"], ["current_course", "Book"],
    ["attendance_percentage", "Attendance %"], ["exam_average_percentage", "Exam avg %"],
    ["homework_completion_percentage", "HW done %"], ["homework_average_grade", "HW avg %"],
    ["overall_progress", "Overall %"], ["overall_letter_grade", "Grade"],
    ["progress_report_rating", "Rating"]],
  certificates: [["certificate_number", "Number"], ["student_name", "Student"], ["course", "Course"],
    ["completion_date", "Completed"], ["issued_date", "Issued"], ["notes", "Notes"]],
};

const MONEY = new Set(["net_amount", "paid_amount", "balance", "outstanding", "amount"]);

export function ReportsTab() {
  const [active, setActive] = useState("students");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const url = active === "progress" ? "/progress/summaries" : `/reports/${active}`;
    api.get(url)
      .then((r) => setData(active === "progress" ? { rows: r.data, total: r.data.length } : r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [active]);

  const cols = COLUMNS[active];
  const rows = data?.rows || [];
  const cell = (row, key) => {
    const v = row[key];
    if (v == null || v === "") return "—";
    if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (MONEY.has(key)) return AFN(v);
    return String(v);
  };

  const summary = () => {
    if (!data) return [];
    if (active === "fees") return [["Billed", AFN(data.total_billed)], ["Collected", AFN(data.total_collected)],
      ["Outstanding", AFN(data.total_outstanding)], ["Collection rate", data.collection_rate == null ? "—" : `${data.collection_rate}%`]];
    if (active === "outstanding") return [["Students with balance", data.students_with_balance],
      ["Total outstanding", AFN(data.total_outstanding)]];
    if (active === "attendance") return [["Records", data.records], ["Students", data.students],
      ["Average attendance", data.average_attendance == null ? "—" : `${data.average_attendance}%`]];
    if (active === "classes") return [["Classes", data.total], ["Active", data.active]];
    if (active === "certificates") return [["Certificates issued", data.total]];
    return [["Total", data.total ?? rows.length]];
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {REPORTS.map((rp) => (
            <Button key={rp.key} data-testid={`report-tab-${rp.key}`} variant={active === rp.key ? "default" : "outline"}
              className={`rounded-full h-9 ${active === rp.key ? "bg-[#0F1E4F] text-white" : ""}`} onClick={() => setActive(rp.key)}>
              {rp.label}
            </Button>
          ))}
        </div>
        <Button data-testid="report-print" variant="outline" className="rounded-full" onClick={() => window.print()}>
          <Printer className="h-4 w-4 mr-1.5" /> Print
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {summary().map(([k, v]) => (
          <Card key={k} className="p-5 rounded-2xl border-slate-200">
            <div className="text-xs uppercase tracking-widest text-slate-500">{k}</div>
            <div className="mt-2 font-serif text-2xl font-black text-[#0F1E4F]">{v ?? "—"}</div>
          </Card>
        ))}
      </div>

      {active === "fees" && data?.by_category?.length > 0 && (
        <Card className="rounded-2xl border-slate-200 overflow-hidden">
          <div className="px-5 pt-5 font-serif font-black text-[#0F1E4F]">By category</div>
          <Table>
            <TableHeader><TableRow><TableHead>Category</TableHead><TableHead>Items</TableHead><TableHead>Billed</TableHead><TableHead>Collected</TableHead><TableHead>Outstanding</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.by_category.map((c) => (
                <TableRow key={c.category} data-testid={`fees-cat-${c.category}`}>
                  <TableCell className="capitalize">{String(c.category).replace(/_/g, " ")}</TableCell>
                  <TableCell>{c.count}</TableCell>
                  <TableCell>{AFN(c.billed)}</TableCell>
                  <TableCell>{AFN(c.collected)}</TableCell>
                  <TableCell>{AFN(c.outstanding)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Card data-testid={`report-table-${active}`} className="rounded-2xl border-slate-200 overflow-x-auto">
        {loading ? <div className="p-10 text-center text-slate-500 text-sm">Loading report…</div>
          : rows.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">
              <FileBarChart className="h-6 w-6 mx-auto mb-2 text-slate-300" /> No data for this report yet.
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow>{cols.map(([k, l]) => <TableHead key={k}>{l}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={row.student_id || row.class_id || row.fee_id || row.certificate_id || i}>
                    {cols.map(([k], j) => (
                      <TableCell key={k} className={j === 0 ? "font-semibold text-[#0F1E4F]" : ""}>
                        {k === "status"
                          ? <Badge className="rounded-full border-0 bg-slate-100 text-slate-700 capitalize">{row[k]}</Badge>
                          : cell(row, k)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
      </Card>
    </div>
  );
}
